import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { TokenService } from 'src/app/shared/utils/token.service';
import { OktaPkceAuthService } from './okta-pkce-auth.service';
import * as OktaAuth from '@okta/okta-auth-js';
import { OktaConstant } from '../constants/okta.constant';
import { AppService } from 'src/app/app.service';
import { CacheResolverService } from '../cache/cache-resolver.service';
import { CacheConstant } from 'src/app/core/cache/cache.constant';
import { AuthValidate } from '../../shared/models/app';

@Injectable({
  providedIn: 'root'
})
export class TokenRefresherService {

  private oktaAuth: OktaAuth = null;
  private timer = null;

  constructor(
    private authService: AuthService,
    private oktaService: OktaPkceAuthService,
    private cacheService: CacheResolverService,
    private appService: AppService,
    private tokenService: TokenService
  ) { }

  startTimer(): Promise<void> {
    if (this.timer) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      this.authService.isAuthenticated().then((auth) => {
        if (!Object.values(auth.authenticated).includes(true)) {
          resolve();
          return;
        }
        
        this.startWatching();
        resolve();
      });
    });
  }

  stopTimer() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  private renewTokens(): Promise<void> {
    return new Promise(async (resolve) => {
      const tokens = await this.renewAccessAndIdTokens();
      if (!tokens.accessToken || !tokens.idToken) {
        resolve();
        return;
      }
      this.clearTokens();
      await this.validateTokens(tokens);
      await this.appService.loadUserDetailsAndRoles();
      resolve();
    });
  }

  private startWatching(): void {
    this.oktaAuth = this.oktaService.getOktaAuth();
    const tokenExpiry = this.tokenService.getTokenExpiry(); // 1 hr
    const currentTimestamp = Date.now();
    const timeBeforeExpiry = 300000; // 5 mins
    const remainingTime = tokenExpiry - currentTimestamp - timeBeforeExpiry;

    this.timer = setTimeout(() => {
      this.renewTokens().then(() => {
        this.stopTimer();
        this.startTimer();
      });
    }, remainingTime);
  }

  private validateTokens(tokens): Promise<void> {
    return new Promise((resolve) => {
      this.tokenService.validate(tokens.accessToken, tokens.idToken).toPromise().then((tokenResponse) => {
        if (!tokenResponse.hasError) {
          this.cacheService.set(CacheConstant.OktaAuth, tokenResponse, 60 * 60 * 1);
          this.tokenService.saveJWT('token', tokenResponse['access_token']);
        }
        resolve();
      });
    });
  }

  private async renewAccessAndIdTokens() {
    const newIdToken = await this.oktaAuth.tokenManager.renew(OktaConstant.IdToken);
    const newAccessToken = await this.oktaAuth.tokenManager.renew(OktaConstant.AccessToken);
    this.oktaAuth.tokenManager.add(OktaConstant.IdToken, newIdToken);
    this.oktaAuth.tokenManager.add(OktaConstant.AccessToken, newAccessToken);
    return {
      idToken: newIdToken.idToken,
      accessToken: newAccessToken.accessToken
    }
  }

  private clearTokens(): void {
    localStorage.removeItem(CacheConstant.OktaAuth);
    localStorage.removeItem('token');
  }
}
